import moment from 'moment';
import { getBusinessDaysDiff, normalizeRichTextValue } from '../../helpers/global';

export const formatAssigneeName = name => {
  if (!name?.trim()) return '';
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0];
  const lastInitial = parts[parts.length - 1][0];
  if (!lastInitial) return parts[0];
  return `${parts[0]} ${lastInitial}.`;
};

export const getOrderTypeColor = orderType => {
  const map = {
    'Lien Search': '#F59E0B',
    Estoppel: '#8B5CF6',
    Survey: '#10B981',
    Other: '#6B7280'
  };
  return map[orderType] || map.Other;
};

export const getOverdueBusinessDays = (dueDate, excludeFridays) => {
  const today = moment().startOf('day');
  const due = moment(dueDate).startOf('day');
  return getBusinessDaysDiff(due, today, excludeFridays);
};

export const getBizDaysLabel = businessDays =>
  `${businessDays} biz day${businessDays !== 1 ? 's' : ''}`;

/** Prefer title; if missing, "Untitled - <description snippet>" for legacy tasks. */
export const getTaskDisplayTitle = task => {
  const title = task?.title != null ? String(task.title).trim() : '';
  if (title) return title;

  const plain = String(task?.description || '')
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/gi, "'")
    .replace(/\s+/g, ' ')
    .trim();

  if (plain) {
    const short = plain.length > 80 ? `${plain.slice(0, 77)}...` : plain;
    return `Untitled - ${short}`;
  }

  return 'Untitled';
};

export const hasTaskDescription = task => Boolean(normalizeRichTextValue(task?.description));

export const getTaskVisualStatus = task => {
  if (task?.status === 'Complete') return 'completed';
  const today = moment().startOf('day');
  const dueDate = moment(task.dueDate).startOf('day');
  if (dueDate.isBefore(today)) return 'overdue';
  return 'pending';
};

/** Shared status badge for search cards, pipeline cards, and all task views. */
export const getTaskStatusBadge = (task, isKanbanView = false) => {
  if (task?.status === 'Complete') {
    return {
      label: 'Completed',
      variant: 'completed',
      background: '#d1fae5',
      textColor: '#065f46',
      cardBorderColor: 'var(--gray)',
      badgeColor: '#065f46',
      badgeBg: '#E7FFF3'
    };
  }

  const excludeFridays = Boolean(task?.excludeFridays);
  const due = moment(task.dueDate).startOf('day');
  const today = moment().startOf('day');

  if (due.isBefore(today)) {
    const overdueDays = Math.max(1, getOverdueBusinessDays(task.dueDate, excludeFridays));
    const overdueLabel = isKanbanView 
      ? `${overdueDays}d` 
      : `${overdueDays} day${overdueDays !== 1 ? 's' : ''}`;
    return {
      label: `${overdueLabel} Overdue`,
      variant: 'overdue',
      background: '#fee2e2',
      textColor: '#991b1b',
      cardBorderColor: '#E24B4A',
      badgeColor: '#991b1b',
      badgeBg: '#FEE2E2'
    };
  }

  if (due.isSame(today, 'day')) {
    return {
      label: 'Due today',
      variant: 'due-today',
      background: '#dbeafe',
      textColor: '#1e40af',
      cardBorderColor: '#185FA5',
      badgeColor: '#1e40af',
      badgeBg: '#dbeafe'
    };
  }

  return {
    label: 'Pending',
    variant: 'pending',
    background: '#fef3c7',
    textColor: '#92400e',
    cardBorderColor: 'var(--gray)',
    badgeColor: '#9A6700',
    badgeBg: '#FFF7D6'
  };
};

/** @deprecated Use getTaskStatusBadge */
export const getSearchItemStatusBadge = getTaskStatusBadge;

export const determineTaskBucket = task => {
  const dueDate = moment(task.dueDate).startOf('day');
  const today = moment().startOf('day');
  const tomorrow = moment().add(1, 'day').startOf('day');
  const endOfWeek = moment().endOf('week');

  if (task.status === 'Complete') {
    return 'pastTasks';
  }

  if (dueDate.isBefore(today)) {
    return 'overdue';
  }
  if (dueDate.isSame(today, 'day')) {
    return 'today';
  }
  if (dueDate.isSame(tomorrow, 'day')) {
    return 'tomorrow';
  }
  if (dueDate.isBefore(endOfWeek) || dueDate.isSame(endOfWeek, 'day')) {
    return 'thisWeek';
  }
  return 'later';
};
